import { useState, useEffect, useRef } from 'react';
import { X, Send } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function FlowCommentSheet({ flowId, onClose, onCommentAdded }) {
  const [comments, setComments] = useState([]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    fetchComments();
    inputRef.current?.focus();
  }, [flowId]);

  const fetchComments = async () => {
    try {
      const token = localStorage.getItem('dharmbook_token');
      const res = await axios.get(`${API}/flow/${flowId}/comments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setComments(res.data);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      const token = localStorage.getItem('dharmbook_token');
      const res = await axios.post(`${API}/flow/${flowId}/comments`, { content: text.trim() }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setComments(prev => [res.data, ...prev]);
      setText('');
      onCommentAdded?.();
    } catch {
      toast.error('Failed to post vichar');
    } finally {
      setSending(false);
    }
  };

  const timeAgo = (date) => {
    const diff = (Date.now() - new Date(date).getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return `${Math.floor(diff / 86400)}d`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center" onClick={onClose} data-testid="flow-comment-sheet">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" />

      {/* Sheet */}
      <div
        className="relative w-full max-w-lg bg-white rounded-t-2xl max-h-[70vh] flex flex-col animate-in slide-in-from-bottom duration-300"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900">Vichar ({comments.length})</h3>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-gray-100 transition-colors">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Comments List */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="w-6 h-6 border-2 border-[#FF9933] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : comments.length === 0 ? (
            <div className="text-center py-10">
              <p className="text-gray-400 text-sm">No vichar yet</p>
              <p className="text-gray-300 text-xs mt-1">Be the first to share your thoughts</p>
            </div>
          ) : (
            comments.map(c => (
              <div key={c.id} className="flex gap-2.5" data-testid={`comment-${c.id}`}>
                <img
                  src={c.user_avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(c.user_name)}&background=FF9933&color=fff`}
                  alt={c.user_name}
                  className="w-8 h-8 rounded-full object-cover flex-shrink-0 mt-0.5"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2">
                    <span className="text-sm font-semibold text-gray-900">{c.user_name}</span>
                    <span className="text-[11px] text-gray-400">{timeAgo(c.created_at)}</span>
                  </div>
                  <p className="text-sm text-gray-700 mt-0.5 leading-relaxed">{c.content}</p>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Input */}
        <div className="border-t border-gray-100 px-4 py-3 flex items-center gap-2">
          <input
            ref={inputRef}
            type="text"
            value={text}
            onChange={e => setText(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSend()}
            placeholder="Share your vichar..."
            className="flex-1 px-4 py-2.5 bg-gray-50 rounded-full text-sm border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#FF9933]/40 focus:border-[#FF9933]"
            data-testid="flow-comment-input"
          />
          <button
            onClick={handleSend}
            disabled={!text.trim() || sending}
            className="p-2.5 bg-[#FF9933] hover:bg-[#E68A00] disabled:bg-gray-300 text-white rounded-full transition-colors"
            data-testid="flow-comment-send"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
